import { Loader2, X } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { fetchLegalDocument } from "./legalDocuments";
import type { LegalDocType } from "./legal-constants";
import { renderMarkdown } from "./renderMarkdown";

interface LegalDocumentDialogProps {
  /** The document to show. `null` keeps the dialog closed. */
  type: LegalDocType | null;
  open: boolean;
  onClose: () => void;
}

/**
 * SPEC-DOCCONSENT-2 — popup that shows the current legal text from
 * `legal_documents`. Lazy-loads on open so the heavy text payload only
 * arrives when the user actually clicks one of the signup links.
 *
 * SPEC-DOCCONSENT-2b — widened to `max-w-2xl w-[92vw]` so the dialog
 * keeps a comfortable reading column on desktop while still respecting
 * mobile viewports; scroll body is `max-h-[75vh]` with internal scroll
 * so the dialog itself never grows past the viewport. The raw markdown
 * is rendered via `renderMarkdown` (above) instead of
 * `whitespace-pre-wrap`, so headings, bullets and inline bold get the
 * right semantic + visual treatment.
 */
export function LegalDocumentDialog({
  type,
  open,
  onClose,
}: LegalDocumentDialogProps) {
  const [loading, setLoading] = React.useState(false);
  const [content, setContent] = React.useState<string | null>(null);
  const [errored, setErrored] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!open || type === null) {
      setContent(null);
      setErrored(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setContent(null);
    setErrored(false);
    fetchLegalDocument(type).then((row) => {
      if (cancelled) return;
      if (!row) {
        setErrored(true);
        setLoading(false);
        return;
      }
      setContent(row.content);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, type]);

  const title =
    type === "terms_of_service"
      ? "Kullanıcı Sözleşmesi"
      : type === "privacy_notice"
        ? "Aydınlatma Metni (KVKK & Çerez)"
        : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="w-[92vw] max-w-3xl sm:max-w-3xl"
        data-testid="legal-doc-dialog"
        // The shadcn DialogContent ships its own internal close X; we
        // render a custom one alongside so the spec-required
        // `legal-doc-close` testid is reachable.
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="absolute top-2 right-2"
              data-testid="legal-doc-close"
              aria-label="Kapat"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogClose>
        </DialogHeader>
        <div
          className="max-h-[75vh] overflow-y-auto px-1 py-2 text-sm leading-relaxed"
          data-testid="legal-doc-content"
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Yükleniyor…
            </div>
          ) : errored ? (
            <p className="py-12 text-center text-destructive">
              Belge yüklenemedi. Lütfen daha sonra tekrar deneyin.
            </p>
          ) : content ? (
            renderMarkdown(content)
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
